
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  PropertySummary, 
  PropertyDetails, 
  ReportHeader, 
  LoadingState, 
  ErrorState,
  StrengthsWeaknesses,
  MarketComparison,
  InvestmentProjection 
} from '@/components/report';

import { toast } from 'sonner';
import { supabase } from "@/integrations/supabase/client";
import PropertyCard from '@/components/report/PropertyCard';
import { calculatePropertyScore } from '@/utils/propertyScoreCalculator';

// Novo tipo para análise do Supabase
interface SupabaseAnalysis {
  status: number;
  property_info: {
    id: string;
    tipo: string;
    bairro: string;
    cidade: string;
    valor_recente: number;
    area_util: number;
    area_total: number | null;
    area_construida: number | null;
    numero_quartos: number;
    numero_suites: number;
    numero_banheiros: number;
    numero_vagas: number;
    descricao: string | null;
    url: string;
    img_url: string;
    condominio: number | null;
  };
  analysis: {
    price_analysis: {
      comparison_to_avg: number;
      comparison_to_avg_per_m2: number;
    };
    neighborhood_stats: {
      avg_price: number;
      max_price: number;
      min_price: number;
      avg_bedrooms: number;
      avg_util_area: number;
      property_count: number;
      avg_price_per_m2: number;
      max_price_per_m2: number;
      min_price_per_m2: number;
    };
    similar_properties: Array<any>;
  };
  evaluation: {
    strengths: string[];
    weaknesses: string[];
  };
}

const ReportPage = () => {
  const [analysis, setAnalysis] = useState<SupabaseAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const getAnalysisData = async () => {
      try {
        setLoading(true);
        const savedPropertyData = sessionStorage.getItem('propertyAnalysis');
        
        if (savedPropertyData) {
          try {
            const parsedData = JSON.parse(savedPropertyData) as SupabaseAnalysis;
            console.log("Loaded analysis data from session storage:", parsedData);
            
            if (parsedData.property_info) {
              setAnalysis(parsedData);
            } else {
              toast.error("Dados do imóvel incompletos. Por favor, realize uma nova análise.");
              navigate('/');
            }
          } catch (parseError) {
            console.error("Error parsing analysis data from session storage:", parseError);
            toast.error("Erro ao processar dados da análise. Por favor, tente novamente.");
            navigate('/');
          }
        } else {
          toast.error("Nenhuma análise encontrada. Por favor, realize uma nova análise.");
          navigate('/');
        }
      } catch (error) {
        console.error("Error loading analysis data:", error);
        toast.error("Erro ao carregar a análise. Por favor, tente novamente.");
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    
    getAnalysisData();
  }, [navigate, location.search]);

  if (loading) return <LoadingState />;
  if (!analysis) return <ErrorState />;

  // Calcular o score do imóvel usando a nossa nova função
  const propertyScore = calculatePropertyScore(analysis);

  // Calcular diferença percentual de preço
  const avgPrice = analysis.analysis.neighborhood_stats.avg_price;
  const propertyPrice = analysis.property_info.valor_recente;
  
  // Calcular o percentual de diferença em relação à média - esse valor será mais preciso
  const priceDiff = avgPrice > 0 ? ((propertyPrice - avgPrice) / avgPrice) * 100 : 0;
  
  const priceVsAvg = {
    percentage: Math.abs(priceDiff).toFixed(1),
    isBelowAvg: priceDiff < 0
  };

  // Preparar dados para os gráficos de comparação
  const priceComparison = [
    { name: 'Imóvel', value: analysis.property_info.valor_recente, isMain: true },
    { name: 'Média', value: analysis.analysis.neighborhood_stats.avg_price },
    { name: 'Máximo', value: analysis.analysis.neighborhood_stats.max_price },
    { name: 'Mínimo', value: analysis.analysis.neighborhood_stats.min_price }
  ];

  const pricePerSqMComparison = [
    { 
      name: 'Imóvel', 
      value: analysis.property_info.valor_recente / analysis.property_info.area_util, 
      isMain: true 
    },
    { 
      name: 'Média', 
      value: analysis.analysis.neighborhood_stats.avg_price_per_m2 
    },
    {
      name: 'Máximo',
      value: analysis.analysis.neighborhood_stats.max_price_per_m2
    },
    {
      name: 'Mínimo',
      value: analysis.analysis.neighborhood_stats.min_price_per_m2
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <ReportHeader />
      
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <PropertySummary 
          property={{
            tipo: analysis.property_info.tipo,
            bairro: analysis.property_info.bairro,
            cidade: analysis.property_info.cidade
          }}
          score={propertyScore}
        />
        
        <div className="grid md:grid-cols-3 gap-8 mb-10">
          <div className="md:col-span-2">
            <PropertyCard 
              property={{
                ...analysis.property_info,
                price: analysis.property_info.valor_recente,
                imageUrl: analysis.property_info.img_url
              }} 
              isMain={true} 
              className="h-full" 
            />
          </div>
          <PropertyDetails 
            property={{
              price: analysis.property_info.valor_recente,
              areaUtil: analysis.property_info.area_util,
              areaConstruida: analysis.property_info.area_construida,
              areaTotal: analysis.property_info.area_total,
              condominium: analysis.property_info.condominio,
              finalidade: analysis.property_info.tipo
            }}
            priceVsAvg={priceVsAvg}
          />
        </div>

        <StrengthsWeaknesses 
          strengths={analysis.evaluation.strengths} 
          weaknesses={analysis.evaluation.weaknesses} 
        />
        
        <MarketComparison 
          priceComparison={priceComparison}
          pricePerSqMComparison={pricePerSqMComparison}
          similarProperties={analysis.analysis.similar_properties}
          searchStrategy="advanced"
        />
        
        <InvestmentProjection 
          investmentProjection={{
            valueIncrease: priceDiff, // Usar o valor calculado priceDiff em vez do valor da API
            years: 5,
            rentalReturn: 0 // Podemos calcular se tivermos dados de aluguel
          }}
          score={propertyScore}
          strengths={analysis.evaluation.strengths}
          weaknesses={analysis.evaluation.weaknesses}
        />
        
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-10">
            <h2 className="text-xl font-semibold mb-4">Detalhes da Análise (Debug)</h2>
            <pre className="bg-gray-100 p-4 rounded overflow-auto text-xs">
              {JSON.stringify(analysis, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPage;
